import React from "react";
import { Reveal, Eyebrow, LotusDivider } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { InterestCta, TextLink } from "../components/Bits";
import ReelStrip from "../components/ReelStrip";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { LAST_EDITION, DIYA_PHOTO, OG_POSTER } from "../lib/constants";

const NOT = [
  {
    title: "Not a satsang",
    body:
      "A satsang gathers to listen and receive. Bhajan jamming hands the melody back to the room — you're not an audience being led in prayer, you're a voice in the chorus. There's no sermon, no hierarchy of the holy.",
  },
  {
    title: "Not a concert",
    body:
      "At a concert the stage performs and the crowd watches. Here the line dissolves. The band holds the groove, but the song only fully arrives when a few hundred people sing it back. Applause isn't the point — participation is.",
  },
  {
    title: "Not a fusion gimmick",
    body:
      "The bhajans stay bhajans. Modern production — a proper sound system, considered light — is in service of the words, never on top of them. Reverence first, polish second.",
  },
];

const STEPS = [
  { n: "01", name: "You hear about it", desc: "Through a friend, a reel, or this page. There's no membership — just a date and a room." },
  { n: "02", name: "You pick a show", desc: "One evening, two showtimes. Choose the one that fits your day and hold your seat." },
  { n: "03", name: "You come, and you sing", desc: "The band starts the drone, the first bhajan lands, and the room takes over. That's the whole format." },
];

export default function WhatIsBhajanJamming() {
  return (
    <>
      <Seo route={ROUTES.pillar} type="article" image={OG_POSTER} />
      <JsonLd data={breadcrumbs([{ name: ROUTES.pillar.crumb, path: ROUTES.pillar.path }])} />

      <PageHero
        eyebrow="The format, explained"
        title={<>What is <span className="gold-grad italic">bhajan jamming?</span></>}
        intro="Bhajan jamming is live, participatory devotional music: traditional bhajans and sufi songs, held by a live band, sung back by everyone in the room. Not a performance you watch — a chorus you join."
      />

      {/* Definition — light section (one per page) */}
      <section className="bg-cream py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="font-serif-italic text-2xl md:text-3xl leading-snug gold-grad text-center">
              A bhajan is a song of devotion. A jam is what happens when a group of people
              make music together without a script.
            </p>
            <p className="mt-8 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Put them together and you get bhajan jamming — an evening where the old songs are
              carried live and the room does the singing. The words are ones many people already
              half-know: a Hanuman Chalisa line, a Shiva bhajan, a sufi refrain that keeps circling
              back until everyone is inside it. There is no test of who knows the lyrics. The
              melody repeats on purpose, so that by the third round you've stopped listening and
              started singing.
            </p>
            <p className="mt-5 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Antara is one way of doing this — a curated, ticketed evening built around the idea.
              It grew out of <TextLink href="https://kashiwithanshu.com/">Kashi with Anshu</TextLink>,
              where the same singing first happened on a boat on the Ganga at dawn, and where the
              format found its shape before it ever met an auditorium.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Origin — the fullest telling. This is the page's differentiator. */}
      <section className="relative overflow-hidden bg-[#0F0A07] py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>The river, not the mall</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-[56px] leading-[1.04]" style={{ color: "#FBF6EE" }}>
                It began on a boat
              </h2>
            </div>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-[1.15fr_0.85fr] gap-12 md:gap-16 items-center">
            <Reveal delay={0.1}>
              <div className="space-y-5 font-body text-base md:text-lg leading-relaxed" style={{ color: "#E8D9C5" }}>
                <p>
                  Most bhajan jamming you'll hear about started in a mall atrium or someone's living
                  room. Antara's began somewhere else entirely — in Kashi, on the Ganga.
                </p>
                <p>
                  The first sessions were not staged at all. A boat pushed off into the river in
                  Varanasi by lamplight and drifted past Manikarnika, and somewhere along the way the
                  singing started. No sound system. No seating plan. No audience — everyone on the
                  water was a voice in it. The song was carried by the group and the current, and it
                  stopped when the boat came back to the ghat.
                </p>
                <p className="font-serif-italic text-xl md:text-2xl" style={{ color: "#E5C880" }}>
                  Antara is that boat, made large enough for a city to sit in.
                </p>
                <p>
                  When those river sessions moved into an auditorium, almost nothing about the idea
                  changed — only the scale. The live band now holds what the current used to; the
                  room sings what the boat once did. That is why an Antara evening feels less like a
                  concert you attend and more like a place you're let into. The original sessions
                  haven't stopped either — they're still part of every{" "}
                  <TextLink href="https://kashiwithanshu.com/the-immersion/">Kashi with Anshu immersion</TextLink>,
                  which you can read more about on our{" "}
                  <TextLink to={ROUTES.kashi.path}>Kashi with Anshu</TextLink> page.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-[#C9A961]/25 to-transparent blur-xl" />
                <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] border" style={{ borderColor: "rgba(201,169,97,0.3)" }}>
                  <img src={DIYA_PHOTO} alt="A lamp floated on the Ganga at Kashi by night — the river where Antara's bhajan jamming began" width="800" height="1000" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Reels — immediately after the origin story */}
      <ReelStrip id="reels" lead="This is what the room sounds like." />

      {/* What it isn't */}
      <section className="bg-[#0F0A07] py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Eyebrow>Three things it isn't</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl" style={{ color: "#FBF6EE" }}>
                Where it sits, exactly
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5 md:gap-6">
            {NOT.map((n, i) => (
              <Reveal key={n.title} delay={i * 0.1}>
                <div className="h-full p-7 md:p-8 rounded-2xl border" style={{ background: "#1C1A17", borderColor: "rgba(201,169,97,0.22)" }}>
                  <h3 className="font-display text-2xl" style={{ color: "#E5C880" }}>{n.title}</h3>
                  <p className="mt-3 font-body text-sm md:text-base leading-relaxed" style={{ color: "#E8D9C5" }}>{n.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* A short history */}
      <section className="bg-ivory py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <Eyebrow>Where it comes from</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">An old habit, newly staged</h2>
            <p className="mt-6 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Communal devotional singing is not new to India — kirtan, qawwali, the evening aarti,
              the temple bhajan mandali have always been group affairs. What's new is the staging:
              taking that participatory instinct out of the temple courtyard and into an auditorium,
              with the production values of a live gig and none of the passivity.
            </p>
            <p className="mt-5 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Antara's own version started in Banaras, in the <TextLink to={ROUTES.kashi.path}>Kashi with Anshu</TextLink>{" "}
              immersions, and then travelled — first to <TextLink to={ROUTES.gurgaon.path}>Gurgaon</TextLink>,
              then to <TextLink to={ROUTES.delhi.path}>Delhi</TextLink> and <TextLink to={ROUTES.chandigarh.path}>Chandigarh</TextLink>.
              You can see how each evening has unfolded in the <TextLink to={ROUTES.editions.path}>editions archive</TextLink>.
            </p>
            <h3 className="mt-10 font-display text-2xl md:text-3xl text-ink">Is it the same as bhajan clubbing?</h3>
            <p className="mt-4 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Close cousins. &ldquo;Bhajan clubbing&rdquo; is the name many people now use for nights where a
              live band plays bhajans and the crowd sings and dances along. Antara calls what it does
              bhajan jamming: the same joy of singing devotional songs together, staged in an auditorium or
              open-air venue with a live band, and built around the whole room singing the refrains —
              from quiet, grounding bhajans to full-voiced ones and back to stillness.
            </p>
            <LotusDivider />
          </Reveal>
        </div>
      </section>

      {/* How it works — a genuine 3-step sequence */}
      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center">
              <Eyebrow>How it works</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">Three steps, honestly</h2>
            </div>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="h-full p-7 rounded-2xl bg-ivory-2 border border-gold/25">
                  <p className="font-display text-3xl gold-grad">{s.n}</p>
                  <h3 className="mt-3 font-display text-2xl text-ink">{s.name}</h3>
                  <p className="mt-2 font-body text-sm md:text-base text-ink-2/85 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 text-center">
              <InterestCta note={`${LAST_EDITION.dateLabel} · ${LAST_EDITION.venue}, ${LAST_EDITION.city}`} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
